const textEncoder = new TextEncoder();
const IMAGE_FILE_EXTENSIONS = new Set([
    'avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'webp'
]);

export const buildZipDownloadFileName = (preferredName, fallbackName = 'carousel-images') => {
    const rawName = String(preferredName || '').trim() || fallbackName;
    const extension = rawName.split('.').pop()?.toLowerCase();
    const withoutExtension = IMAGE_FILE_EXTENSIONS.has(extension) || extension === 'zip'
        ? rawName.slice(0, -(extension.length + 1))
        : rawName;
    const safeName = withoutExtension
        .replace(/[\\/:*?"<>|]/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 180);

    return `${safeName || fallbackName}.zip`;
};

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let index = 0; index < 256; index += 1) {
        let value = index;
        for (let bit = 0; bit < 8; bit += 1) {
            value = (value & 1) ? (0xEDB88320 ^ (value >>> 1)) : (value >>> 1);
        }
        table[index] = value >>> 0;
    }
    return table;
})();

const crc32 = bytes => {
    let checksum = 0xFFFFFFFF;
    for (let index = 0; index < bytes.length; index += 1) {
        checksum = CRC_TABLE[(checksum ^ bytes[index]) & 0xFF] ^ (checksum >>> 8);
    }
    return (checksum ^ 0xFFFFFFFF) >>> 0;
};

const writeUint16 = (view, offset, value) => view.setUint16(offset, value, true);
const writeUint32 = (view, offset, value) => view.setUint32(offset, value >>> 0, true);

// Stores files without compression. Images are already compressed, so this avoids
// spending CPU while still giving the browser a single download.
export const createZipBlob = async files => {
    const entries = [];
    let localOffset = 0;

    for (const file of files || []) {
        if (!file?.blob || !file?.name) {
            continue;
        }

        const name = textEncoder.encode(String(file.name));
        const data = new Uint8Array(await file.blob.arrayBuffer());
        const header = new Uint8Array(30 + name.length);
        const view = new DataView(header.buffer);
        writeUint32(view, 0, 0x04034B50);
        writeUint16(view, 4, 20);
        writeUint16(view, 6, 0x0800); // UTF-8 file names
        writeUint16(view, 8, 0); // Store; image files are already compressed.
        writeUint32(view, 14, crc32(data));
        writeUint32(view, 18, data.length);
        writeUint32(view, 22, data.length);
        writeUint16(view, 26, name.length);
        name.forEach((value, index) => {
            header[30 + index] = value;
        });

        entries.push({ header, name, data, offset: localOffset, crc: crc32(data) });
        localOffset += header.length + data.length;
    }

    const centralDirectory = entries.map(entry => {
        const header = new Uint8Array(46 + entry.name.length);
        const view = new DataView(header.buffer);
        writeUint32(view, 0, 0x02014B50);
        writeUint16(view, 4, 20);
        writeUint16(view, 6, 20);
        writeUint16(view, 8, 0x0800);
        writeUint16(view, 10, 0);
        writeUint32(view, 16, entry.crc);
        writeUint32(view, 20, entry.data.length);
        writeUint32(view, 24, entry.data.length);
        writeUint16(view, 28, entry.name.length);
        writeUint32(view, 42, entry.offset);
        entry.name.forEach((value, index) => {
            header[46 + index] = value;
        });
        return header;
    });
    const centralDirectorySize = centralDirectory.reduce((size, entry) => size + entry.length, 0);
    const endOfCentralDirectory = new Uint8Array(22);
    const endView = new DataView(endOfCentralDirectory.buffer);
    writeUint32(endView, 0, 0x06054B50);
    writeUint16(endView, 8, entries.length);
    writeUint16(endView, 10, entries.length);
    writeUint32(endView, 12, centralDirectorySize);
    writeUint32(endView, 16, localOffset);

    return new Blob([
        ...entries.flatMap(entry => [entry.header, entry.data]),
        ...centralDirectory,
        endOfCentralDirectory
    ], { type: 'application/zip' });
};
